// Rounding task generator and simple localStorage-backed recording
export type NearestRule = { type: "nearest"; unit: number };
export type DecimalsRule = { type: "decimals"; places: number };
export type ConversionRule = {
  type: "conversion";
  from: string;
  to: string;
  factor: number;
};
export type MultiplicationRule = { type: "multiplication" };
export type Rule =
  | NearestRule
  | DecimalsRule
  | ConversionRule
  | MultiplicationRule;

export type Task = {
  id: string;
  text: string;
  answer: string;
  rule: Rule;
  createdAt: number;
};

export type Attempt = {
  id: string;
  user: string;
  taskId: string;
  taskText: string;
  rule: Rule;
  submission: string;
  correct: boolean;
  timeMs: number;
  attemptNumber: number;
  focusLost: boolean;
  timestamp: number;
  events?: AttemptEvent[];
};

export type AttemptEvent = {
  type: string;
  ts: number;
  data?: Record<string, any> | string | number | null;
};

const MAGNITUDES: Array<[number, number]> = [
  [2, 10],
  [3, 10],
  [3, 100],
  [4, 10],
  [4, 100],
  [4, 1000],
  [5, 100],
  [5, 1000],
  [6, 1000],
];

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 9);
}

function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function formatNumber(n: number) {
  if (Number.isInteger(n)) return n.toLocaleString("en-US").replace(/,/g, " ");
  return String(n);
}

function formatDecimal(n: number) {
  if (Number.isInteger(n)) return n.toLocaleString("en-US").replace(/,/g, " ");
  const s = n.toFixed(6);
  return s.replace(/\.?0+$/, "");
}

export function roundTo(value: number, rule: Rule): string {
  if (rule.type === "nearest") {
    const unit = rule.unit;
    const r = Math.round(value / unit) * unit;
    return formatNumber(r);
  }
  if (rule.type === "decimals") {
    const places = rule.places;
    // Use toFixed to match expected number of decimal places
    return Number(value).toFixed(places);
  }
  // conversion: apply factor and format the result
  if (rule.type === "conversion") {
    return formatDecimal(value * rule.factor);
  }
  // fallback
  return String(value);
}

export function generateIntegerTask(): Task {
  const [digits, unit] =
    MAGNITUDES[Math.floor(Math.random() * MAGNITUDES.length)]!;
  const low = Math.pow(10, digits - 1);
  const high = Math.pow(10, digits) - 1;
  const n = randInt(low, high);
  const rule: NearestRule = { type: "nearest", unit };
  const text = `Round ${formatNumber(n)} to the nearest ${unit}.`;
  const answer = roundTo(n, rule);
  return { id: uid(), text, answer, rule, createdAt: Date.now() };
}

export function generateDecimalTask(): Task {
  const whole = randInt(0, 99);
  const decimals = randInt(3, 6);
  const frac = randInt(0, Math.pow(10, decimals) - 1);
  const sFrac = String(frac).padStart(decimals, "0");
  const val = Number(`${whole}.${sFrac}`);
  const placesOptions = [1, 2, 3, 4];
  const places =
    placesOptions[Math.floor(Math.random() * placesOptions.length)]!;
  const text = `Round ${val} to ${places} decimal place${places !== 1 ? "s" : ""}.`;
  const answer = roundTo(val, { type: "decimals", places });
  return {
    id: uid(),
    text,
    answer,
    rule: { type: "decimals", places },
    createdAt: Date.now(),
  };
}

export function generateRoundingTask(): Task {
  if (Math.random() < 0.6) return generateIntegerTask();
  return generateDecimalTask();
}

export function generateConversionTask(): Task {
  const CONVERSIONS: Array<{
    from: string;
    to: string;
    factor: number;
    min: number;
    max: number;
  }> = [
    { from: "mL", to: "L", factor: 1 / 1000, min: 10, max: 20000 },
    { from: "m", to: "km", factor: 1 / 1000, min: 1, max: 200000 },
    { from: "mm", to: "m", factor: 1 / 1000, min: 1, max: 200000 },
    { from: "cm", to: "m", factor: 1 / 100, min: 1, max: 200000 },
    { from: "g", to: "kg", factor: 1 / 1000, min: 1, max: 200000 },
  ];

  const conv = CONVERSIONS[Math.floor(Math.random() * CONVERSIONS.length)]!;
  const value = randInt(conv.min, conv.max);
  const result = value * conv.factor;
  const rule: ConversionRule = {
    type: "conversion",
    from: conv.from,
    to: conv.to,
    factor: conv.factor,
  };
  const text = `Convert ${formatNumber(value)} ${conv.from} to ${conv.to}.`;
  const answer = formatDecimal(result);
  return { id: uid(), text, answer, rule, createdAt: Date.now() };
}

export function generateMultiplicationTask(): Task {
  const a = randInt(1, 9);
  const b = randInt(1, 9);
  const rule: MultiplicationRule = { type: "multiplication" };
  const text = `${a} times ${b} =`;
  const answer = String(a * b);
  return { id: uid(), text, answer, rule, createdAt: Date.now() };
}

export function generateTask(): Task {
  const r = Math.random();
  if (r < 0.2) return generateConversionTask();
  if (r < 0.7) return generateIntegerTask();
  return generateDecimalTask();
}

// Storage: use server-side JSON API. Fall back to localStorage if server unavailable.
const ATTEMPTS_KEY = "rounding_attempts";
const STATE_PREFIX = "rounding_state_"; // + username
const API_ROOT = "/api/rounding";

function getAuthHeaders(): Record<string, string> {
  try {
    const token = localStorage.getItem("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function dispatchUnauthorized() {
  try {
    window.dispatchEvent(new CustomEvent("app:unauthorized"));
  } catch {}
}

export async function getAllAttempts(): Promise<Attempt[]> {
  try {
    const res = await fetch(`${API_ROOT}/attempts`, {
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      dispatchUnauthorized();
      return [];
    }
    if (!res.ok) return [];
    const data = await res.json();
    // keep backward compatibility: server may return full array or a paginated object
    if (Array.isArray(data)) return data as Attempt[];
    if (data && Array.isArray(data.items)) return data.items as Attempt[];
    return [];
  } catch {
    try {
      const raw = localStorage.getItem(ATTEMPTS_KEY);
      return raw ? (JSON.parse(raw) as Attempt[]) : [];
    } catch {
      return [];
    }
  }
}

export type AttemptPage = {
  items: Attempt[];
  total: number;
  page: number;
  pageSize: number;
};

export async function getAttempts(opts?: {
  page?: number;
  pageSize?: number;
  user?: string | null;
  text?: string | null;
  correct?: "all" | "correct" | "incorrect" | null;
  start?: string | null; // YYYY-MM-DD or timestamp
  end?: string | null; // YYYY-MM-DD or timestamp
}): Promise<AttemptPage> {
  const params = new URLSearchParams();
  if (opts?.page) params.set("page", String(opts.page));
  if (opts?.pageSize) params.set("pageSize", String(opts.pageSize));
  if (opts?.user) params.set("user", opts.user);
  if (opts?.text) params.set("text", opts.text);
  if (opts?.correct && opts.correct !== "all")
    params.set("correct", opts.correct);
  if (opts?.start) params.set("start", opts.start);
  if (opts?.end) params.set("end", opts.end);

  try {
    const res = await fetch(`${API_ROOT}/attempts?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      dispatchUnauthorized();
      return {
        items: [],
        total: 0,
        page: opts?.page ?? 1,
        pageSize: opts?.pageSize ?? 20,
      };
    }
    if (!res.ok)
      return {
        items: [],
        total: 0,
        page: opts?.page ?? 1,
        pageSize: opts?.pageSize ?? 20,
      };
    const data = await res.json();
    if (Array.isArray(data))
      return {
        items: data,
        total: data.length,
        page: 1,
        pageSize: data.length,
      };
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total:
        typeof data.total === "number"
          ? data.total
          : Array.isArray(data.items)
            ? data.items.length
            : 0,
      page: typeof data.page === "number" ? data.page : (opts?.page ?? 1),
      pageSize:
        typeof data.pageSize === "number"
          ? data.pageSize
          : (opts?.pageSize ?? 20),
    } as AttemptPage;
  } catch {
    return {
      items: [],
      total: 0,
      page: opts?.page ?? 1,
      pageSize: opts?.pageSize ?? 20,
    };
  }
}

export async function saveAllAttempts(items: Attempt[]) {
  try {
    const putRes = await fetch(`${API_ROOT}/attempts`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify(items),
    });
    if (putRes.status === 401) dispatchUnauthorized();
  } catch {
    try {
      localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(items));
    } catch {
      // ignore
    }
  }
}

export async function recordAttempt(
  a: Omit<Attempt, "id" | "timestamp" | "attemptNumber">,
): Promise<Attempt> {
  try {
    const res = await fetch(`${API_ROOT}/attempts`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify(a),
    });
    if (res.status === 401) {
      dispatchUnauthorized();
      throw new Error("Unauthorized");
    }
    if (res.ok) return (await res.json()) as Attempt;
  } catch {
    // fall through to local fallback
  }

  // Fallback: localStorage (offline)
  try {
    const raw = localStorage.getItem(ATTEMPTS_KEY);
    const attempts = raw ? (JSON.parse(raw) as Attempt[]) : [];
    const same = attempts.filter(
      (x) => x.user === a.user && x.taskId === a.taskId,
    ).length;
    const attemptObj: Attempt = {
      id: uid(),
      timestamp: Date.now(),
      ...a,
      attemptNumber: same + 1,
    } as Attempt;
    attempts.push(attemptObj);
    localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(attempts));
    return attemptObj;
  } catch {
    const attemptObj: Attempt = {
      id: uid(),
      timestamp: Date.now(),
      ...a,
      attemptNumber: 1,
    } as Attempt;
    return attemptObj;
  }
}

export async function getUserAttempts(user: string) {
  const all = await getAllAttempts();
  return all.filter((a) => a.user === user);
}

export async function saveLastTaskForUser(
  user: string,
  task: Task,
  completed = false,
) {
  try {
    const res = await fetch(`${API_ROOT}/state/${encodeURIComponent(user)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ task, completed }),
    });
    if (res.status === 401) dispatchUnauthorized();
  } catch {
    try {
      const key = STATE_PREFIX + user;
      const payload = { task, completed };
      localStorage.setItem(key, JSON.stringify(payload));
    } catch {
      // ignore
    }
  }
}

export async function loadLastTaskForUser(
  user: string,
): Promise<{ task: Task; completed: boolean } | null> {
  try {
    const res = await fetch(`${API_ROOT}/state/${encodeURIComponent(user)}`, {
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      dispatchUnauthorized();
      return null;
    }
    if (!res.ok) return null;
    return (await res.json()) as { task: Task; completed: boolean } | null;
  } catch {
    try {
      const key = STATE_PREFIX + user;
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}

export async function clearAllRoundingData() {
  try {
    const res = await fetch(`${API_ROOT}/attempts`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    if (res.status === 401) dispatchUnauthorized();
  } catch {
    try {
      localStorage.removeItem(ATTEMPTS_KEY);
    } catch {}
  }
}
